import React, { FC, useState, useRef, useEffect, useMemo } from "react";
import type { Clip, Track } from "../../types";
import { DEFAULT_LIBRARY } from "./constants";
import { getMediaDurationFromUrl, createNewClip } from "./helpers";
import { Play, Pause, Trash2, Pencil, Plus, Copy } from "lucide-react";
import { functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import { AddMediaModal } from "./AddMediaModal";
import { EditLibraryItemModal } from "./EditLibraryItemModal";

// FIX: The User type should be imported from the firebase namespace, not directly from firebase/compat/auth.
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { getMasterTemplates } from '../../firestore';
import type { MasterTemplate } from '../../types';


interface LibraryTabProps {
    onAddLibraryItem?: (item: any, category: any, mode?: string) => void;
    allClips: Clip[];
    setAllClips: React.Dispatch<React.SetStateAction<Clip[]>>;
    tracks: Track[];
    user: firebase.User | null;
    selectedClip: Clip | null;
    onMasterSaveAndExit?: () => Promise<void>;
    libraryRefreshTrigger?: number;
    preloadedAssets?: Record<string, string>;
    isLibraryAssetsComplete?: boolean;
}

const getCustomLibrary = httpsCallable(functions, 'getCustomLibrary');
const addCustomLibraryItem = httpsCallable(functions, 'addCustomLibraryItem');
const deleteCustomLibraryItem = httpsCallable(functions, 'deleteCustomLibraryItem');
const updateCustomLibraryItem = httpsCallable(functions, 'updateCustomLibraryItem');

const LibraryTab: FC<LibraryTabProps> = ({
    onAddLibraryItem = (item: any, category: any, mode?: string) => { },
    allClips,
    setAllClips,
    tracks,
    user,
    selectedClip,
    onMasterSaveAndExit,
    libraryRefreshTrigger = 0,
    preloadedAssets = {},
    isLibraryAssetsComplete = false,
}) => {
    const [editingCategories, setEditingCategories] = useState<Record<string, boolean>>({});
    const [playingAudio, setPlayingAudio] = useState<{ id: string; element: HTMLAudioElement } | null>(null);
    const audioRef = useRef(playingAudio);
    audioRef.current = playingAudio; // Keep ref updated for cleanup

    const [userLibraryItems, setUserLibraryItems] = useState<any[]>([]);
    const [masterTemplates, setMasterTemplates] = useState<MasterTemplate[]>([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<any | null>(null);
    const [fileForModal, setFileForModal] = useState<File | null>(null);
    const [modalCategory, setModalCategory] = useState<'intro-outro' | 'audio' | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const refreshLibrary = () => {
        if (user) {
            getCustomLibrary().then((result: any) => {
                if (result.data && Array.isArray(result.data)) {
                    setUserLibraryItems(result.data);
                }
            }).catch(console.error);

            // Fetch Master Templates directly from Firestore
            getMasterTemplates().then((templates) => {
                setMasterTemplates(templates);
            }).catch(e => console.error("Failed to fetch master templates:", e));
        }
    };

    useEffect(() => {
        refreshLibrary();
    }, [user]);

    // Refresh library when trigger changes (after master save)
    useEffect(() => {
        if (libraryRefreshTrigger > 0) {
            refreshLibrary();
        }
    }, [libraryRefreshTrigger]);

    const mergedLibrary = useMemo(() => {
        // Build a map of saved master templates by ID for quick lookup
        const masterTemplateMap = new Map<string, any>();
        masterTemplates.forEach(t => masterTemplateMap.set(t.id, t));

        const library = DEFAULT_LIBRARY.map(category => {
            if (['intro-outro', 'audio', 'synthes', 'panels'].includes(category.id)) {
                const customItems = userLibraryItems.filter(item =>
                    item.category === category.id ||
                    (category.id === 'intro-outro' && !item.category)
                );

                // Override default items with saved master template data + inject preloaded bloba
                const mergedDefaultItems = category.items.map(item => {
                    const savedTemplate = masterTemplateMap.get(item.id);
                    const finalItem = { ...item };

                    if (savedTemplate) {
                        finalItem.syntheData = savedTemplate.syntheData;
                        finalItem.duration = savedTemplate.duration || item.duration;
                        finalItem.name = savedTemplate.name || item.name;
                    }

                    // Inject preloaded blob URLs if available
                    if (finalItem.url && preloadedAssets[finalItem.url]) {
                        finalItem.url = preloadedAssets[finalItem.url];
                    }
                    if (finalItem.syntheData && finalItem.syntheData.clips) {
                        finalItem.syntheData = {
                            ...finalItem.syntheData,
                            clips: finalItem.syntheData.clips.map((clip: any) => ({
                                ...clip,
                                url: (clip.url && preloadedAssets[clip.url]) ? preloadedAssets[clip.url] : clip.url
                            }))
                        }
                    }

                    return finalItem;
                });

                // Inject blob URLs into custom items as well
                const finalCustomItems = customItems.map(item => {
                    const mappedItem = { ...item };
                    if (mappedItem.url && preloadedAssets[mappedItem.url]) {
                        mappedItem.url = preloadedAssets[mappedItem.url];
                    }
                    return mappedItem;
                });

                return {
                    ...category,
                    items: [...mergedDefaultItems, ...finalCustomItems]
                };
            }
            return category;
        });

        // Add "Mes Modèles" category for templates that DON'T match a default ID
        const defaultIds = new Set(DEFAULT_LIBRARY.flatMap(c => c.items.map((i: any) => i.id)));
        const extraTemplates = masterTemplates.filter(t => !defaultIds.has(t.id));
        if (extraTemplates.length > 0) {
            library.push({
                id: 'my-templates',
                name: 'Mes Modèles',
                icon: <div className="p-1 bg-purple-500/20 text-purple-400 rounded">M</div>,
                items: extraTemplates.map(t => ({
                    id: t.id,
                    name: t.name,
                    type: t.type,
                    category: 'my-templates',
                    syntheData: t.syntheData,
                    info: 'Modèle perso',
                    url: null,
                    isCustom: true
                }))
            });
        }

        return library;
    }, [userLibraryItems, masterTemplates, preloadedAssets]);

    const toggleEditing = (categoryId: string) => {
        setEditingCategories(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
    };

    const handleAddMediaClick = (category: 'intro-outro' | 'audio') => {
        setModalCategory(category);
        setFileForModal(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const handleAddTemplate = async (category: any) => {
        if (!user) {
            alert("Connectez-vous pour sauvegarder des modèles.");
            return;
        }

        if (category.id === 'synthes' || category.id === 'panels') {
            const expectedType = category.id === 'synthes' ? 'synthe' : 'panel';
            let initialData = {
                type: expectedType,
                syntheData: null,
                duration: null,
                info: "Modèle vide"
            };

            let itemName = category.id === 'synthes' ? 'Nouveau Synthé' : 'Nouveau Panel';
            let isFromSelection = false;

            // Check if selected clip matches category
            if (selectedClip && selectedClip.type === expectedType) {
                // Use selected clip data
                initialData.syntheData = selectedClip.syntheData;
                initialData.duration = selectedClip.duration;
                initialData.info = "Depuis la sélection";
                isFromSelection = true;

                // Keep prompt for selection-based creation as user might want to name their specific saved style
                const name = prompt("Nom du modèle (ex: Base Dior 2024) :");
                if (!name) return;
                itemName = name;
            } else {
                // Direct creation (empty model) - NO CONFIRMATION, NO PROMPT
            }

            try {
                const result: any = await addCustomLibraryItem({
                    name: itemName,
                    category: category.id,
                    type: initialData.type,
                    syntheData: initialData.syntheData,
                    duration: initialData.duration,
                    info: initialData.info,
                    url: null
                });

                // Immediately update local state and open editor
                if (result && result.data && result.data.id) {
                    const newItem = {
                        id: result.data.id,
                        name: itemName,
                        category: category.id,
                        type: initialData.type,
                        syntheData: initialData.syntheData,
                        duration: initialData.duration,
                        info: initialData.info,
                        url: null,
                        isCustom: true
                    };

                    setUserLibraryItems(prev => [...prev, newItem]);
                    setEditingItem(newItem);
                }

                refreshLibrary();
            } catch (error) {
                console.error("Failed to add template:", error);
                alert("Erreur lors de la sauvegarde.");
            }
        } else {
            // Existing logic for audio/intro
            handleAddMediaClick(category.id as any);
        }
    };

    const handleDeleteItem = async (itemId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!confirm("Supprimer cet élément de la bibliothèque ?")) return;
        try {
            await deleteCustomLibraryItem({ itemId });
            refreshLibrary();
        } catch (error) {
            console.error("Failed to delete item:", error);
        }
    };

    const handleDuplicate = async (item: any, e: React.MouseEvent) => {
        e.stopPropagation();
        const newName = prompt("Nom de la copie :", `${item.name} copy`);
        if (!newName) return;

        try {
            await addCustomLibraryItem({
                name: newName,
                category: item.category,
                type: item.type,
                syntheData: item.syntheData,
                duration: item.duration,
                info: item.info,
                url: item.url
            });
            refreshLibrary();
        } catch (error) {
            console.error("Duplicate failed:", error);
            alert("Erreur lors de la duplication.");
        }
    };

    const handleEditItem = (item: any, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingItem(item);
    };

    const handleAudioPreview = (item: any) => {
        if (playingAudio && playingAudio.id === item.id) {
            playingAudio.element.pause();
            setPlayingAudio(null);
        } else {
            if (playingAudio) {
                playingAudio.element.pause();
            }
            const audio = new ((window as any).Audio)(item.url);
            audio.onended = () => setPlayingAudio(null);
            audio.play().catch(console.error);
            setPlayingAudio({ id: item.id, element: audio });
        }
    };

    const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && modalCategory) {
            const isVideo = file.type.startsWith('video/');
            const isAudio = file.type.startsWith('audio/');

            if ((modalCategory === 'intro-outro' && isVideo) || (modalCategory === 'audio' && isAudio)) {
                setFileForModal(file);
                setIsAddModalOpen(true);
            } else {
                alert(`Veuillez sélectionner un fichier de type ${modalCategory === 'intro-outro' ? 'vidéo' : 'audio'}.`);
            }
        }
        if (e.target) e.target.value = ''; // Reset input to allow re-selecting the same file
    };


    const handleDeleteCustomItem = async (itemId: string) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce média ? Cette action est irréversible.")) {
            return;
        }
        try {
            await deleteCustomLibraryItem({ itemId });
            setUserLibraryItems(prev => prev.filter(item => item.id !== itemId));
        } catch (error) {
            console.error("Error deleting item:", error);
            alert("Impossible de supprimer le média.");
        }
    };


    const handleIntroOutroToggle = async (item: any) => {
        if (!item.clipRole || !item.url) return;

        const existingClip = allClips.find(c => c.name === item.name);

        if (item.clipRole === 'intro') {
            const introShiftDuration = 2; // Fixed 2-second push for intro

            if (existingClip) {
                // REMOVE INTRO
                const shiftAmount = -introShiftDuration;
                let updatedClips = allClips.filter(c => c.id !== existingClip.id);

                updatedClips = updatedClips.map(clip => {
                    const newStart = Math.max(0, clip.start + shiftAmount);
                    let newSubtitles = clip.subtitles;
                    if (clip.subtitles) {
                        newSubtitles = {};
                        Object.keys(clip.subtitles).forEach(lang => {
                            newSubtitles![lang] = clip.subtitles![lang].map(sub => ({
                                ...sub,
                                start: sub.start + shiftAmount,
                                end: sub.end + shiftAmount,
                            }));
                        });
                    }
                    return { ...clip, start: newStart, subtitles: newSubtitles };
                });
                setAllClips(updatedClips);
            } else {
                // ADD INTRO
                const originalMediaDuration = await getMediaDurationFromUrl(item.url);

                const shiftedClips = allClips.map(clip => {
                    const newStart = clip.start + introShiftDuration;
                    let newSubtitles = clip.subtitles;
                    if (clip.subtitles) {
                        newSubtitles = {};
                        Object.keys(clip.subtitles).forEach(lang => {
                            newSubtitles![lang] = clip.subtitles![lang].map(sub => ({
                                ...sub,
                                start: sub.start + introShiftDuration,
                                end: sub.end + introShiftDuration,
                            }));
                        });
                    }
                    return { ...clip, start: newStart, subtitles: newSubtitles };
                });

                const newIntroClip = createNewClip({
                    name: item.name,
                    url: item.url,
                    type: 'video',
                    start: 0,
                    duration: originalMediaDuration,
                    originalDuration: originalMediaDuration,
                    trackId: tracks.find(t => t.accepts.includes('video') && !['t1', 's1', 'z1'].includes(t.id))?.id || 'v1',
                    clipRole: 'intro',
                });
                setAllClips([newIntroClip, ...shiftedClips]);
            }
        } else if (item.clipRole === 'outro') {
            if (existingClip) {
                // REMOVE OUTRO
                setAllClips(allClips.filter(c => c.id !== existingClip.id));
            } else {
                // ADD OUTRO
                const duration = await getMediaDurationFromUrl(item.url);
                const timelineEnd = allClips.reduce((max, c) => Math.max(max, c.start + c.duration), 0);
                const newStartTime = Math.max(0, timelineEnd - 1.5);

                const newOutroClip = createNewClip({
                    name: item.name,
                    url: item.url,
                    type: 'video',
                    start: newStartTime,
                    duration: duration,
                    originalDuration: duration,
                    trackId: tracks.find(t => t.accepts.includes('video') && !['t1', 's1', 'z1'].includes(t.id))?.id || 'v1',
                    clipRole: 'outro',
                });
                setAllClips([...allClips, newOutroClip]);
            }
        }
    };

    return (
        <div id="panel-library" role="tabpanel" aria-labelledby="tab-library" className="h-full flex flex-col">
            <input type="file" ref={fileInputRef} onChange={handleFileSelected} accept={modalCategory === 'audio' ? 'audio/*' : 'video/*'} hidden />
            {isAddModalOpen && fileForModal && modalCategory && (
                <AddMediaModal
                    file={fileForModal}
                    category={modalCategory}
                    onClose={() => setIsAddModalOpen(false)}
                    onSuccess={(newItem) => {
                        setUserLibraryItems(prev => [...prev, newItem]);
                        setIsAddModalOpen(false);
                    }}
                />
            )}
            <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900">Library</h2>
                <p className="text-xs text-gray-500">Ressources LVMH disponibles</p>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4">
                {mergedLibrary.map((category) => {
                    const isEditing = editingCategories[category.id] || false;
                    return (
                        <div key={category.id} className="mb-6">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-xl">{category.icon}</span>
                                <h3 className="font-semibold text-sm text-gray-900 uppercase tracking-wide">
                                    {category.name}
                                    {(category as any).disabled && <span className="ml-2 text-gray-400 normal-case font-normal text-xs">(soon)</span>}
                                </h3>
                                <div className="flex-1 h-px bg-gray-200" />
                                {(category.id === 'intro-outro' || category.id === 'audio') ? (
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => handleAddMediaClick(category.id as any)} className="p-1 hover:bg-gray-100 rounded-md" title="Importer">
                                            <Plus className="w-4 h-4 text-gray-600" />
                                        </button>
                                        <button onClick={() => toggleEditing(category.id)} className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1">
                                            <Pencil className="w-3 h-3" /> {isEditing ? 'Terminé' : 'Modifier'}
                                        </button>
                                    </div>
                                ) : (category.id === 'synthes' || category.id === 'panels') ? (
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => handleAddTemplate(category)} className="p-1 hover:bg-gray-100 rounded-md" title="Créer un modèle depuis la sélection">
                                            <Plus className="w-4 h-4 text-gray-600" />
                                        </button>
                                        <button onClick={() => toggleEditing(category.id)} className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1">
                                            <Pencil className="w-3 h-3" /> {isEditing ? 'Terminé' : 'Modifier'}
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                            <div className="space-y-2">
                                {category.items.map((item) => {
                                    const itemWithUrl = item as any;
                                    // Cast to any to access isCustom safely if types aren't updated
                                    const isCustom = (item as any).isCustom;
                                    const isDisabled = !!(category as any).disabled;
                                    // Synthes/Panels are draggable if they have syntheData or if we implement drag for them
                                    // For now, let's assume they are handled via click mainly, but drag is good too.
                                    // Existing code checked itemWithUrl.url, but synthes don't have URL.
                                    const isDraggable = (!!itemWithUrl.url || category.id === 'synthes' || category.id === 'panels') && !isDisabled;
                                    const isIntroOutro = itemWithUrl.clipRole === 'intro' || itemWithUrl.clipRole === 'outro';
                                    const isActive = isIntroOutro && allClips.some(c => c.name === item.name);

                                    return (
                                        <div
                                            key={item.id}
                                            draggable={isDraggable}
                                            onDragStart={(e) => {
                                                if (!isDraggable) {
                                                    e.preventDefault();
                                                    return;
                                                }

                                                let durationInSeconds = 0;
                                                if (item.duration) {
                                                    if (typeof item.duration === 'number') {
                                                        durationInSeconds = item.duration;
                                                    } else if (typeof item.duration === 'string') {
                                                        const parts = item.duration.split(':').map(Number);
                                                        if (parts.length === 2) {
                                                            durationInSeconds = parts[0] * 60 + parts[1];
                                                        } else if (parts.length === 3) {
                                                            durationInSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
                                                        }
                                                    }
                                                }

                                                // Construct drag data for Synthe/Panel
                                                const dragData = {
                                                    type: category.id === 'synthes' ? 'synthe' : category.id === 'panels' ? 'panel' : itemWithUrl.type,
                                                    name: item.name,
                                                    url: itemWithUrl.url,
                                                    duration: durationInSeconds || undefined,
                                                    originalDuration: durationInSeconds || undefined,
                                                    syntheData: (item as any).syntheData // Pass syntheData
                                                };
                                                (e.dataTransfer as any).setData('application/json', JSON.stringify(dragData));
                                                (e.dataTransfer as any).effectAllowed = 'copy';
                                            }}
                                            className={`w-full flex items-center gap-3 p-3 rounded-xl border transition ${isDisabled
                                                ? 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
                                                : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                                } ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
                                            onDoubleClick={(e) => {
                                                // Enable double click to edit master templates
                                                if (category.id === 'synthes' || category.id === 'panels') {
                                                    // Navigate into master for content editing
                                                    onAddLibraryItem && onAddLibraryItem(
                                                        { ...item, category: item.category || category.id },
                                                        category,
                                                        "edit"
                                                    );
                                                    // Also open the edit popup for renaming / saving
                                                    handleEditItem({ ...item, category: item.category || category.id }, e);
                                                }
                                            }}
                                        >
                                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center shrink-0">
                                                {category.id === 'audio' ? (
                                                    <button onClick={() => handleAudioPreview(item)} className="w-full h-full flex items-center justify-center rounded-lg hover:bg-gray-300/50 transition">
                                                        {(playingAudio && playingAudio.id === item.id) ? <Pause size={20} /> : <Play size={20} />}
                                                    </button>
                                                ) : (
                                                    <span className="text-lg">{category.icon}</span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-gray-900 truncate">{item.name}</div>
                                                <div className="text-xs text-gray-500">{item.duration ? `${item.duration} • ` : ""}{item.info}</div>
                                            </div>

                                            {isEditing ? (
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={(e) => handleEditItem({ ...item, category: item.category || category.id }, e)}
                                                        className="p-2 transition rounded-full text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                                                        title="Modifier"
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                    <button onClick={(e) => handleDuplicate(item, e)} className="p-2 text-gray-400 hover:bg-green-50 hover:text-green-600 rounded-full transition" title="Dupliquer">
                                                        <Copy className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => handleDeleteItem(item.id, e)}
                                                        className={`p-2 transition rounded-full ${isCustom ? 'text-gray-400 hover:bg-red-50 hover:text-red-600' : 'text-gray-200 cursor-not-allowed'}`}
                                                        title={isCustom ? "Supprimer" : "Impossible de supprimer un élément par défaut"}
                                                        disabled={!isCustom}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ) : isIntroOutro ? (
                                                isEditing ? <div className="w-9 h-9"></div> : (
                                                    <button
                                                        onClick={() => handleIntroOutroToggle(item)}
                                                        className={`px-3 py-1.5 text-white text-xs rounded-lg transition-all font-bold w-24 text-center ${isActive ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}
                                                    >
                                                        {isActive ? '− RETIRER' : '+ AJOUTER'}
                                                    </button>
                                                )
                                            ) : (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onAddLibraryItem && onAddLibraryItem(item, category, "fit"); }}
                                                    className="w-8 h-8 flex items-center justify-center bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition"
                                                    title="Ajouter à la timeline"
                                                    disabled={isDisabled}
                                                >
                                                    <Plus className="w-5 h-5" />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )
                })}
            </div>
            {editingItem && (
                <EditLibraryItemModal
                    item={editingItem}
                    selectedClip={selectedClip}
                    onClose={() => setEditingItem(null)}
                    onUpdate={async (itemId, changes) => {
                        // Check if current item is custom
                        const isCustom = editingItem.isCustom;

                        if (isCustom) {
                            await updateCustomLibraryItem({ itemId, changes });
                        } else {
                            // If default item, CREATE a new custom item with the changes
                            await addCustomLibraryItem({
                                name: changes.name || editingItem.name,
                                category: editingItem.category,
                                type: editingItem.type,
                                syntheData: changes.syntheData || editingItem.syntheData,
                                duration: changes.duration || editingItem.duration,
                                info: editingItem.info,
                                url: editingItem.url
                            });
                        }
                        refreshLibrary();
                        setEditingItem(null);
                    }}
                    onMasterSaveAndExit={onMasterSaveAndExit}
                />
            )}
        </div>
    );
};
export default LibraryTab;
